(() => {
  'use strict';

  const api = window.IzzatThemeV2;
  if (!api || customElements.get('sticky-atc')) return;

  const overlaySelector = [
    'cart-drawer.active:not([aria-hidden="true"])',
    'menu-drawer details[open]',
    'details[open] > .search-modal',
    'quick-add-modal[open]',
    'modal-dialog[open]',
    'product-modal[open]',
    'product-lightbox:not([hidden]):not([aria-hidden="true"])',
    'pickup-availability-drawer[open]',
    'cart-notification.active',
  ].join(',');
  const escapeSelector = (value) => window.CSS?.escape?.(value)
    || String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  const numberValue = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const stickyInstances = () => [...(document.querySelectorAll?.('sticky-atc') || [])]
    .filter((instance) => instance?.isConnected !== false);
  let coordinationActive = false;

  const primaryStickyInstance = (instances = stickyInstances()) => {
    const enabled = instances.filter((instance) => instance.responsiveEnabled?.() !== false);
    const productOwned = enabled.filter((instance) => instance.dataset?.stickyRole === 'product');
    return productOwned[0] || enabled[0] || null;
  };

  const coordinateStickyInstances = (fallbackInstance) => {
    if (coordinationActive) return;
    coordinationActive = true;
    try {
      const instances = stickyInstances();
      if (fallbackInstance && fallbackInstance.isConnected !== false && !instances.includes(fallbackInstance)) {
        instances.push(fallbackInstance);
      }
      const primary = primaryStickyInstance(instances);
      instances.forEach((instance) => instance.updateVisibility(primary));
      primary?.syncFloatingControlState?.();
    } finally {
      coordinationActive = false;
    }
  };

  class IzzatStickyAtc extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      this._lastVisible = false;
      this._lastAnnouncement = '';
      this._onClick = this.handleClick.bind(this);
      this._onStateChange = (event) => {
        this.sync(event);
        this.scheduleVisibilityUpdate();
      };
      this._onViewportChange = this.scheduleVisibilityUpdate.bind(this);
      this._onSectionLoad = (event) => {
        if (!event?.target || event.target.contains?.(this) || this.contains?.(event.target)) {
          this.disconnectVisibilityObserver();
          this.sync();
        }
        this.scheduleVisibilityUpdate();
      };
      this._onSectionUnload = (event) => {
        if (event?.target?.contains?.(this)) this.disconnectVisibilityObserver();
        this.scheduleVisibilityUpdate();
      };
      this.addEventListener('click', this._onClick);
      document.addEventListener('izzat:offer-change', this._onStateChange);
      document.addEventListener('shopify:section:load', this._onSectionLoad);
      document.addEventListener('shopify:section:unload', this._onSectionUnload);
      window.addEventListener('scroll', this._onViewportChange, { passive: true });
      window.addEventListener('resize', this._onViewportChange);
      window.addEventListener('orientationchange', this._onViewportChange);
      window.visualViewport?.addEventListener?.('resize', this._onViewportChange);
      window.visualViewport?.addEventListener?.('scroll', this._onViewportChange);
      this._reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)') || null;
      this._onMotionChange = () => this.syncMotionPreference();
      this._reducedMotion?.addEventListener?.('change', this._onMotionChange);
      this._overlayMutationObservers = [];
      if (typeof MutationObserver === 'function') {
        const overlayTargets = document.querySelectorAll([
          'cart-drawer',
          'menu-drawer details',
          '.search-modal',
          'quick-add-modal',
          'modal-dialog',
          'product-modal',
          'product-lightbox',
          'pickup-availability-drawer',
          'cart-notification',
        ].join(','));
        overlayTargets.forEach((target) => {
          const observer = new MutationObserver(this._onViewportChange);
          observer.observe(target, {
            attributes: true,
            attributeFilter: ['class', 'open', 'aria-hidden', 'hidden'],
          });
          this._overlayMutationObservers.push(observer);
        });
      }
      this.syncMotionPreference();
      this.sync();
      coordinateStickyInstances(this);
      this.scheduleVisibilityUpdate();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      document.removeEventListener('izzat:offer-change', this._onStateChange);
      document.removeEventListener('shopify:section:load', this._onSectionLoad);
      document.removeEventListener('shopify:section:unload', this._onSectionUnload);
      window.removeEventListener('scroll', this._onViewportChange);
      window.removeEventListener('resize', this._onViewportChange);
      window.removeEventListener('orientationchange', this._onViewportChange);
      window.visualViewport?.removeEventListener?.('resize', this._onViewportChange);
      window.visualViewport?.removeEventListener?.('scroll', this._onViewportChange);
      this._reducedMotion?.removeEventListener?.('change', this._onMotionChange);
      this._overlayMutationObservers?.forEach?.((observer) => observer.disconnect());
      this._overlayMutationObservers = [];
      this.disconnectVisibilityObserver();
      if (this._visibilityFrame) {
        if (typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(this._visibilityFrame);
        else window.clearTimeout(this._visibilityFrame);
      }
      this._visibilityFrame = null;
      this._initialized = false;
      this.syncFloatingControlState();
      coordinateStickyInstances();
    }

    syncMotionPreference() {
      const duration = this._reducedMotion?.matches ? 0 : Math.max(0, numberValue(this.dataset.transitionMs, 150));
      this.style?.setProperty?.('--sticky-atc-effective-transition-ms', `${duration}ms`);
    }

    disconnectVisibilityObserver() {
      this._visibilityObserver?.disconnect();
      this._visibilityObserver = undefined;
      this._observedPrimaryButton = undefined;
    }

    primarySubmitButton() {
      return this.productForm()?.form?.querySelector?.('[type="submit"][name="add"], .product-form__submit') || null;
    }

    observePrimaryButton(button) {
      if (this.dataset.displayMode !== 'after_scroll' || typeof window.IntersectionObserver !== 'function') {
        this.disconnectVisibilityObserver();
        return;
      }
      if (button === this._observedPrimaryButton && this._visibilityObserver) return;
      this.disconnectVisibilityObserver();
      if (!button) return;
      this._observedPrimaryButton = button;
      this._visibilityObserver = new window.IntersectionObserver(
        () => this.scheduleVisibilityUpdate(),
        { threshold: [0, 1] },
      );
      this._visibilityObserver.observe(button);
    }

    scheduleVisibilityUpdate() {
      if (this._visibilityFrame) return;
      const requestFrame = typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (callback) => window.setTimeout(callback, 0);
      this._visibilityFrame = requestFrame(() => {
        this._visibilityFrame = null;
        coordinateStickyInstances(this);
      });
    }

    overlayIsOpen() {
      return Boolean(document.querySelector?.(overlaySelector));
    }

    footerElement() {
      return document.querySelector?.('.shopify-section-group-footer-group, footer') || null;
    }

    viewportHeight() {
      return window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight || 0;
    }

    syncViewportOffset() {
      const viewport = window.visualViewport;
      const keyboardOffset = viewport
        ? Math.max(0, (window.innerHeight || viewport.height) - viewport.height - (viewport.offsetTop || 0))
        : 0;
      const configuredOffset = this.isMobile() ? Math.max(0, numberValue(this.dataset.mobileBottomOffset)) : 0;
      this.style?.setProperty?.('--sticky-atc-viewport-offset', `${keyboardOffset + configuredOffset}px`);
    }

    footerBehavior() {
      return ['hide', 'lift', 'keep'].includes(this.dataset.footerBehavior)
        ? this.dataset.footerBehavior
        : 'keep';
    }

    footerState() {
      const footer = this.footerElement();
      const viewportHeight = this.viewportHeight();
      const rect = footer?.getBoundingClientRect?.();
      const intersects = Boolean(rect && rect.top < viewportHeight && rect.bottom > 0);
      const lift = intersects ? Math.max(0, viewportHeight - rect.top) : 0;
      return { intersects, lift };
    }

    isMobile() {
      return window.matchMedia?.('(max-width: 749px)').matches ?? (window.innerWidth || 0) < 750;
    }

    responsiveEnabled() {
      return this.isMobile()
        ? this.dataset.enableMobile !== 'false'
        : this.dataset.enableDesktop !== 'false';
    }

    primaryInstance() {
      return primaryStickyInstance() || this;
    }

    syncFloatingControlState() {
      const hasVisibleSticky = stickyInstances().some((instance) => {
        const ariaHidden = instance.getAttribute?.('aria-hidden') ?? instance['aria-hidden'];
        return ariaHidden === 'false' && instance.inert !== true;
      });
      document.documentElement?.toggleAttribute?.('data-izzat-sticky-atc-visible', hasVisibleSticky);
    }

    updateVisibility(primary = this.primaryInstance()) {
      const mode = this.dataset.displayMode || 'always';
      const threshold = Math.max(0, numberValue(this.dataset.displayThreshold));
      const scrollTop = document.documentElement.scrollTop || window.scrollY || 0;
      const revealBuffer = Math.max(0, numberValue(this.dataset.revealBuffer, 24));
      const isPrimary = primary === this;
      const responsiveEnabled = this.responsiveEnabled();
      let visible = isPrimary && responsiveEnabled && !this.overlayIsOpen();
      if (Boolean(this.hasAttribute?.('data-izzat-sticky-suppressed')) !== !isPrimary) {
        this.toggleAttribute('data-izzat-sticky-suppressed', !isPrimary);
      }
      if (!isPrimary || !responsiveEnabled) this.disconnectVisibilityObserver();

      if (visible && mode === 'after_scroll') {
        const mainButton = this.primarySubmitButton();
        this.observePrimaryButton(mainButton);
        const boundary = this._lastVisible ? revealBuffer : -revealBuffer;
        visible = Boolean(mainButton && mainButton.getBoundingClientRect().bottom <= boundary);
      } else if (visible && mode === 'after_pixels') {
        visible = scrollTop >= threshold;
      } else if (visible && mode === 'after_percent') {
        const scrollable = Math.max(1, document.documentElement.scrollHeight - this.viewportHeight());
        visible = (scrollTop / scrollable) * 100 >= threshold;
      }

      const footer = this.footerState();
      const footerBehavior = this.footerBehavior();
      if (footer.intersects && footerBehavior === 'hide') visible = false;
      const lift = footerBehavior === 'lift' ? footer.lift : 0;
      this.style?.setProperty?.('--sticky-atc-lift', `${lift}px`);
      this.syncViewportOffset();
      this.style.transform = visible
        ? lift > 0 ? `translateY(-${lift}px)` : 'translateY(0)'
        : 'translateY(110%)';
      const nextAriaHidden = visible ? 'false' : 'true';
      const currentAriaHidden = this.getAttribute?.('aria-hidden') ?? this['aria-hidden'];
      if (currentAriaHidden !== nextAriaHidden) this.setAttribute('aria-hidden', nextAriaHidden);
      if (this.inert !== !visible) this.toggleAttribute('inert', !visible);
      this._lastVisible = visible;
      this.syncFloatingControlState();
      return visible;
    }

    productForm() {
      return api.sectionProductForm(this.dataset.section);
    }

    selectedQuantity() {
      const state = api.offerState(this.dataset.section);
      if (state.quantity) return api.quantityValue(state.quantity);
      const sectionId = escapeSelector(this.dataset.section || '');
      const selectedBreak = document.querySelector?.(`quantity-breaks[data-section="${sectionId}"] input[name="quantity"]:checked`);
      if (selectedBreak) return api.quantityValue(selectedBreak.value);
      const form = this.productForm()?.form;
      if (form?.dataset?.izzatQuantity) return api.quantityValue(form.dataset.izzatQuantity);
      if (typeof FormData !== 'function' || !form) return 1;
      return api.quantityValue(new FormData(form).get('quantity'));
    }

    selectedTotal(quantity) {
      const sectionId = escapeSelector(this.dataset.section || '');
      const selected = document.querySelector?.(`quantity-breaks[data-section="${sectionId}"] input[name="quantity"]:checked`);
      const label = selected?.id ? document.querySelector?.(`label[for="${escapeSelector(selected.id)}"]`) : null;
      const breaks = document.querySelector?.(`quantity-breaks[data-section="${sectionId}"]`);
      const unitPrice = numberValue(breaks?.dataset?.basePrice || this.dataset.unitPrice);
      const percentageLeft = numberValue(label?.dataset?.percentageLeft, 1);
      const fixedDiscount = numberValue(label?.dataset?.fixedDiscount);
      return Math.max(0, Math.round(unitPrice * quantity * percentageLeft - fixedDiscount));
    }

    currentVariantLabel() {
      const select = this.querySelector?.('select');
      return select?.selectedOptions?.[0]?.textContent?.trim() || '';
    }

    announce(message) {
      const normalized = String(message || '').replace(/\s+/g, ' ').trim();
      if (!normalized || normalized === this._lastAnnouncement) return false;
      const region = this.querySelector?.('[data-izzat-sticky-status]');
      if (!region) return false;
      region.textContent = normalized;
      this._lastAnnouncement = normalized;
      return true;
    }

    sync(event) {
      if (event?.detail?.sectionId && event.detail.sectionId !== this.dataset.section) return;
      const quantity = this.selectedQuantity();
      const quantityTarget = this.querySelector?.('[data-izzat-sticky-quantity]');
      if (quantityTarget) {
        quantityTarget.textContent = `${quantity}×`;
        const template = quantityTarget.dataset[quantity === 1 ? 'quantityOne' : 'quantityOther'];
        quantityTarget.setAttribute('aria-label', (template || String(quantity)).replaceAll('[count]', String(quantity)));
      }
      const priceTarget = this.querySelector?.(`[data-izzat-sticky-total], .main-atc-price-span-${escapeSelector(this.dataset.section || '')}`);
      const formattedTotal = api.offerState(this.dataset.section).formattedTotal;
      if (priceTarget) {
        priceTarget.textContent = formattedTotal || api.formatMoney(
          this.selectedTotal(quantity),
          this.dataset.moneyFormat,
          this.dataset.currencyCode,
          this.dataset.moneyLocale,
          this.dataset.shopCurrency,
        );
      }
      this.dataset.quantity = String(quantity);
      const button = this.querySelector?.('.sticky-atc__add_button');
      const state = [
        this.currentVariantLabel(),
        quantityTarget?.getAttribute?.('aria-label') || String(quantity),
        priceTarget?.textContent?.trim(),
        button?.disabled ? button?.textContent?.trim() : '',
      ].filter(Boolean).join(', ');
      this.announce(state);
    }

    headerOffset() {
      const headers = document.querySelectorAll?.('.shopify-section-header-sticky, sticky-header, [data-sticky-type="always"]') || [];
      return [...headers].reduce((offset, header) => {
        const rect = header.getBoundingClientRect?.();
        return rect && rect.bottom > 0 ? Math.max(offset, rect.bottom) : offset;
      }, 0);
    }

    scrollAlignment(destination) {
      const alignment = ['start', 'center', 'nearest'].includes(this.dataset.scrollAlignment)
        ? this.dataset.scrollAlignment
        : 'start';
      const rect = destination.getBoundingClientRect();
      const viewportHeight = this.viewportHeight();
      const headerOffset = this.headerOffset();
      const configuredOffset = numberValue(this.dataset.scrollOffset);
      if (alignment === 'nearest' && rect.top >= headerOffset && rect.bottom <= viewportHeight) return null;
      if (alignment === 'center') {
        return (window.scrollY || 0) + rect.top - Math.max(0, (viewportHeight - rect.height) / 2);
      }
      return (window.scrollY || 0) + rect.top - headerOffset - configuredOffset;
    }

    handleClick(event) {
      const button = event?.target?.closest?.('.sticky-atc__add_button, .sticky-atc__scroll-btn');
      if (!button) return;
      if (this.dataset.scrollBtn === 'true') {
        event.preventDefault();
        const selector = this.dataset.scrollDestination || '';
        const destination = document.getElementById?.(selector)
          || document.querySelector?.(`[data-block-id="${escapeSelector(selector)}"]`)
          || document.querySelector?.(selector);
        if (!destination) return;
        const top = this.scrollAlignment(destination);
        if (top == null) return;
        const behavior = this._reducedMotion?.matches ? 'auto' : 'smooth';
        window.scrollTo?.({ top: Math.max(0, top), behavior });
        return;
      }
      const productForm = this.productForm();
      if (!productForm?.handleSubmit) {
        if (button.tagName === 'A' && button.href) return;
        event.preventDefault();
        button.setAttribute?.('aria-disabled', 'true');
        button.disabled = true;
        return;
      }
      this.sync();
      productForm.handleSubmit({
        preventDefault: () => event.preventDefault?.(),
        submitter: button,
      });
    }
  }

  customElements.define('sticky-atc', IzzatStickyAtc);
})();
